import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../css/Newsletter.css';
import HtmlPreview from '../components/HtmlPreview';
import generateEmailHtml from '../components/Template';
import Modal from '../components/Modal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import EmailSelector from '../components/EmailSelector';
import { Editor } from '@tinymce/tinymce-react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import CustomModal from '../components/CustomModal'; 
import NewsletterExamples from '../components/NewsletterExamples';
import Statistics from '../components/Statistics';
import api from '../components/api';
import BeheerdersportaalSidebar from '../components/BeheerdersportaalSidebar';


// Debug API requests
api.interceptors.request.use(
  request => {
    console.log('Starting API Request:', request.url, request.method);
    return request;
  },
  error => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  response => {
    console.log('API Response:', response.status, response.statusText);
    return response;
  },
  error => {
    console.error('API Response Error:', error.response ? {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data,
      url: error.config.url,
      method: error.config.method
    } : error.message);
    return Promise.reject(error);
  }
);

function Newsletter() {
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState('');
  const [fromEmail] = useState('bestuurpolderzicht@gmail.com');
  const [content, setContent] = useState('<p>Beste bewoners,</p><p>Dit is een voorbeeld <a href="https://www.nu.nl" target="_blank" rel="noopener noreferrer">newsletter</a> van VVE Polderzicht.</p>');
  const [subject, setSubject] = useState('');
  const [summary, setSummary] = useState(null);
  const [log, setLog] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEmailSelectorOpen, setIsEmailSelectorOpen] = useState(false);
  const [showExamplesModal, setShowExamplesModal] = useState(false);
  const [showStatisticsModal, setShowStatisticsModal] = useState(false); 
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [attachments, setAttachments] = useState([]);
  const [showAttachmentError, setShowAttachmentError] = useState(false);
  const [attachmentErrorMessage, setAttachmentErrorMessage] = useState('');
  const [sendingInProgress, setSendingInProgress] = useState(false);
  const [currentEmailIndex, setCurrentEmailIndex] = useState(0);
  const [totalEmails, setTotalEmails] = useState(0);
  
  const htmlPreviewRef = useRef();
  const fileInputRef = useRef(null);
  const lastContentRef = useRef(null);
  const navigate = useNavigate();

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  // Define fetchSavedNewsletters before using it in useEffect
  const fetchSavedNewsletters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${apiUrl}/get-newsletters`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      console.log('Newsletters fetched successfully:', response.data.length);
    } catch (error) {
      console.error('Failed to fetch saved newsletters:', error);
    }
  };

  useEffect(() => {
    fetchSavedNewsletters();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  const handleSelectEmails = (selectedEmails) => {
    const emailsList = selectedEmails.join(', ');
    setEmails(emailsList);
  };
  
  const toggleEmailSelector = () => {
    setIsEmailSelectorOpen(prevState => !prevState);
  };

  const toggleExamplesModal = () => {
    setShowExamplesModal(prevState => !prevState);
  };

  const toggleStatisticsModal = () => {
    setShowStatisticsModal(prevState => !prevState);
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate files (size limit: 10MB per file)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
    const invalidFiles = files.filter(file => file.size > MAX_FILE_SIZE);
    
    if (invalidFiles.length > 0) {
      setShowAttachmentError(true);
      setAttachmentErrorMessage(`De volgende bestanden overschrijden de limiet van 10MB: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }
    
    // Add new files to existing attachments
    setAttachments(prev => [...prev, ...files]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Remove an attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  // Send a single email with a delay
const sendSingleEmail = async (email, emailIndex, totalEmails, results) => {
  try {
    // Update the current email index BEFORE starting to send
    setCurrentEmailIndex(emailIndex + 1);
    setLog(prevLog => [...prevLog, `Nieuwsbrief verzenden naar ${email}... (${emailIndex + 1}/${totalEmails})`]);
    
    // Create FormData object to handle file uploads
    const formData = new FormData();
    formData.append('email', email);
    formData.append('fromEmail', fromEmail);
    formData.append('subject', subject);
    
    // Replace links with tracking links
    const modifiedContent = content.replace(
      /href="(https?:\/\/[^\s]+)"/g,
      (match, url) => `href="${apiUrl}/track-click?email=${encodeURIComponent(email)}&link=${encodeURIComponent(url)}&subject=${encodeURIComponent(subject)}"`
    );
    
    // IMPORTANT FIX: Wrap the content with generateEmailHtml to apply styling
    formData.append('content', generateEmailHtml(modifiedContent));
    
    // Add each attachment to the FormData
    attachments.forEach(file => {
      formData.append('attachments', file);
    });
    
    // Use multipart/form-data for sending the newsletter with attachments
    const response = await api.post(`${apiUrl}/api/send-newsletter-with-attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    if (response.status === 200) {
      results.push({ email, status: 'Succes', response: response.data });
      setLog(prevLog => [...prevLog, `✅ Nieuwsbrief succesvol verzonden naar ${email}.`]);
    } else {
      results.push({ email, status: 'Mislukt', error: response.data });
      setLog(prevLog => [...prevLog, `❌ Kon nieuwsbrief niet verzenden naar ${email}.`]);
    }
    
    return results;
  } catch (error) {
    results.push({ email, status: 'Mislukt', error: error.message });
    setLog(prevLog => [...prevLog, `❌ Kon nieuwsbrief niet verzenden naar ${email}. Fout: ${error.message}`]);
    return results;
  }
};

  // Send the newsletter to selected recipients with delays between emails
const sendNewsletter = async () => {
  if (!emails || !subject) {
    alert('Vul alstublieft ontvangers en een onderwerp in voor uw nieuwsbrief.');
    return;
  }
  
  // Additional validation
  if (subject.trim().length < 3) {
    alert('Het onderwerp moet minimaal 3 tekens bevatten.');
    return;
  }
  
  if (content.trim().length < 10) {
    alert('De inhoud van de nieuwsbrief is te kort.');
    return;
  }

  // Relatieve links werken niet in een mailprogramma: er is geen basis-URL, dus
  // de link is dood bij de ontvanger. Blokkeer het versturen in plaats van het
  // achteraf te ontdekken.
  const relatieveLinks = [...content.matchAll(/href="([^"]*)"/gi)]
    .map(m => m[1].trim())
    .filter(href => href && !/^(https?:|mailto:|tel:|#)/i.test(href));

  if (relatieveLinks.length > 0) {
    alert(
      'De nieuwsbrief bevat links zonder volledige URL. In een mailprogramma zijn die niet aanklikbaar.\n\n' +
      relatieveLinks.map(h => `  href="${h}"`).join('\n') +
      '\n\nGebruik een volledige link, bijvoorbeeld https://www.vvepolderzicht.nl/cv-controle'
    );
    return;
  }

  if (!window.confirm(`U staat op het punt een nieuwsbrief te versturen naar ${emails.split(',').length} ontvangers. Wilt u doorgaan?`)) {
    return;
  }
  
  // Reset and show sending modal
  setLog([]);
  setSummary(null);
  setShowModal(true);
  setSendingInProgress(true);
  
  const emailList = emails.split(',').map(email => email.trim()).filter(email => email);
  setTotalEmails(emailList.length);
  setCurrentEmailIndex(0); // Initialize to 0
  
  let results = [];
  
  try {
    // Update log to show we're preparing to send
    setLog(prevLog => [...prevLog, `Voorbereiding voor het verzenden van nieuwsbrief naar ${emailList.length} ontvangers...`]);
    
    // Process emails sequentially with delays
    for (let i = 0; i < emailList.length; i++) {
      const email = emailList[i];
      
      // Send the email and wait for the result
      results = await sendSingleEmail(email, i, emailList.length, results);
      
      // Add a delay between emails to prevent being flagged as spam (3-5 seconds)
      if (i < emailList.length - 1) {
        const delayTime = 4000 + Math.random() * 2000; // 4-6 seconds
        setLog(prevLog => [...prevLog, `⏱️ Pauze van ${Math.round(delayTime/1000)} seconden om spam-detectie te voorkomen...`]);
        await new Promise(resolve => setTimeout(resolve, delayTime));
      }
    }
    
    // Update summary stats
    setSummary(results);
    setLog(prevLog => [...prevLog, 'Versturen van nieuwsbrief voltooid.']);
    
    // Calculate success rate
    const successCount = results.filter(r => r.status === 'Succes').length;
    const totalCount = results.length;
    const successRate = Math.round((successCount / totalCount) * 100);
    
    setLog(prevLog => [...prevLog, `Verzendstatistieken: ${successCount}/${totalCount} succesvol (${successRate}%)`]);
  } catch (error) {
    console.error('Algemene fout bij verzenden:', error);
    setLog(prevLog => [...prevLog, `❌ Algemene fout bij verzenden: ${error.message}`]);
  } finally {
    setSendingInProgress(false);
  }
};

  const saveNewsletter = async () => {
    if (!subject || !content) {
      alert('Vul alstublieft een titel en inhoud in voordat u opslaat.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await api.post(`${apiUrl}/save-newsletter`, {
        title: subject,
        content,
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      alert('Nieuwsbrief succesvol opgeslagen');
      fetchSavedNewsletters();
    } catch (error) {
      console.error('Error saving newsletter:', error);
      alert('Kon nieuwsbrief niet opslaan.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('loginTimestamp');
    // Redirect to home page, not login
    navigate('/home');
  };

  const openExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSelectNewsletter = (newsletter) => {
    if (newsletter.title !== subject || newsletter.content !== lastContentRef.current) {
      setSubject(newsletter.title);
      lastContentRef.current = newsletter.content;
      setContent(newsletter.content);
    }
    setShowExamplesModal(false);
  };

  const exportToPDF = () => {
    if (!subject) {
      alert('Vul eerst een onderwerp in voor uw nieuwsbrief');
      return;
    }
    
    const input = htmlPreviewRef.current;
    if (!input) {
      alert('Kan de voorvertoning niet exporteren. Probeer de pagina te verversen.');
      return;
    }
    
    setLoading(true);
    
    setTimeout(() => {
      html2canvas(input, { 
        useCORS: true, 
        logging: true,
        scale: 2 // Better quality for PDF
      })
        .then(canvas => {
          const imgData = canvas.toDataURL('image/png');
          const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm'
          });
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
          
          pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
          pdf.save(`${subject.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error exporting to PDF:', error);
          alert('Fout bij exporteren naar PDF: ' + error.message);
          setLoading(false);
        });
    }, 1000);
  };
  
  // Test send to a single email before sending to all recipients
  const testSendToSelf = async () => {
    if (!subject || !content) {
      alert('Vul alstublieft een onderwerp en inhoud in voordat u een test verzendt.');
      return;
    }
    
    // Ask for test email
    const testEmail = prompt('Voer uw e-mailadres in voor de test:', fromEmail);
    if (!testEmail || !testEmail.includes('@')) {
      alert('Ongeldig e-mailadres.');
      return;
    }
    
    try {
      setLoading(true);
      
      // Create FormData for attachments
      const formData = new FormData();
      formData.append('email', testEmail);
      formData.append('fromEmail', fromEmail);
      formData.append('subject', `[TEST] ${subject}`);
      
      // IMPORTANT FIX: Wrap the content with generateEmailHtml to apply styling
      formData.append('content', generateEmailHtml(content));
      
      // Add attachments
      attachments.forEach(file => {
        formData.append('attachments', file);
      });
      
      // Send test email
      const response = await api.post(`${apiUrl}/api/send-newsletter-with-attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.status === 200) {
        alert(`Test-nieuwsbrief is verzonden naar ${testEmail}`);
      } else {
        alert('Fout bij verzenden van test-nieuwsbrief');
      }
    } catch (error) {
      console.error('Test send error:', error);
      alert(`Fout bij verzenden van test: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="beheer-wrapper">
  <BeheerdersportaalSidebar>
      {/* Main content */}
      <main className="dashboard newsletter-page">
        <h1>Nieuwsbrief Management</h1>
        
        <div className="newsletter-main-container">
          {/* Top action button - Select emails */}
          <div className="select-emails-container">
            <button 
              className="primary-button full-width" 
              onClick={toggleEmailSelector} 
              disabled={loading || sendingInProgress}
            >
              Selecteer ontvangers ({emails ? emails.split(',').filter(e => e.trim()).length : 0})
            </button>
          </div>

          <div className="newsletter-container">
            <div className="form-container">
              <h2>Nieuwsbrief Invoer</h2>
              <textarea
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                placeholder="Ontvangers (gescheiden door komma's)"
                className="newsletter-textarea"
                style={{ minHeight: '100px' }}
                disabled={loading || sendingInProgress}
              ></textarea>
              <input
                type="text"
                value={subject || ''}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Onderwerp van e-mail"
                className="newsletter-input"
                disabled={loading || sendingInProgress}
              />
              <Tabs>
                <TabList>
                  <Tab>Rich Text Editor</Tab>
                  <Tab>HTML Editor</Tab>
                </TabList>
                <TabPanel>
                  <Editor
                    apiKey="8czevb54iigf73fsgvpmro69urh483qlnlqmot23u4p1c3fn"
                    value={content}
                    onEditorChange={(newContent) => setContent(newContent)}
                    disabled={loading || sendingInProgress}
                    init={{
                      height: 300,
                      menubar: true,
                      // Laat URLs met rust. Zonder dit maakt TinyMCE van een
                      // absolute link naar het eigen domein een relatieve
                      // ("https://www.vvepolderzicht.nl/x" -> "x"), wat in een
                      // browser werkt maar in een mailprogramma een dode link is.
                      convert_urls: false,
                      relative_urls: false,
                      remove_script_host: false,
                      plugins: [
                        'advlist autolink lists link image charmap print preview anchor',
                        'searchreplace visualblocks code fullscreen',
                        'insertdatetime media table paste code help wordcount'
                      ],
                      toolbar:
                        'undo redo | formatselect | bold italic backcolor | \
                        alignleft aligncenter alignright alignjustify | \
                        bullist numlist outdent indent | removeformat | link image | help',
                      style_formats: [
                        { title: 'Heading 1', format: 'h1' },
                        { title: 'Heading 2', format: 'h2' },
                        { title: 'Heading 3', format: 'h3' },
                        { title: 'Paragraph', format: 'p' },
                      ],
                      content_style: `
                        @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600&display=swap');
                        body { font-family: 'Open Sans', sans-serif; font-size:14px; }
                      `,
                      font_formats:
                        "Open Sans=Open Sans, sans-serif; Arial=arial,helvetica,sans-serif; Times New Roman=times new roman,times; Courier New=courier new,courier;",
                    }}
                  />
                </TabPanel>
                <TabPanel>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Nieuwsbrief HTML inhoud"
                    className="newsletter-textarea"
                    style={{ minHeight: '300px' }}
                    disabled={loading || sendingInProgress}
                  ></textarea>
                </TabPanel>
              </Tabs>

              {/* Attachments Section */}
              <div className="attachments-section">
                <h3>Bijlagen</h3>
                <div className="attachment-uploader">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    multiple
                    className="file-input"
                    id="attachment-input"
                    disabled={loading || sendingInProgress}
                  />
                  <label 
                    htmlFor="attachment-input" 
                    className={`custom-file-upload ${(loading || sendingInProgress) ? 'disabled' : ''}`}
                  >
                    <span className="icon">📎</span> Bijlagen toevoegen
                  </label>
                  <span className="file-limits">Max 10MB per bestand</span>
                </div>
                
                {showAttachmentError && (
                  <div className="attachment-error">
                    <p>{attachmentErrorMessage}</p>
                    <button onClick={() => setShowAttachmentError(false)}>×</button>
                  </div>
                )}
                
                {attachments.length > 0 && (
                  <div className="attachment-list">
                    <h4>Bestanden om toe te voegen ({attachments.length})</h4>
                    <ul>
                      {attachments.map((file, index) => (
                        <li key={index} className="attachment-item">
                          <span className="attachment-icon">📄</span>
                          <span className="attachment-name">{file.name}</span>
                          <span className="attachment-size">({formatFileSize(file.size)})</span>
                          <button 
                            className="remove-attachment" 
                            onClick={() => removeAttachment(index)}
                            title="Bijlage verwijderen"
                            disabled={loading || sendingInProgress}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Primary action buttons */}
              <div className="primary-button-container">
                <button 
                  onClick={sendNewsletter} 
                  className="primary-button" 
                  disabled={loading || sendingInProgress}
                >
                  {loading ? 'Bezig...' : 'Verzenden'}
                </button>
                <button 
                  onClick={exportToPDF} 
                  className="primary-button" 
                  disabled={loading || sendingInProgress}
                >
                  Exporteren naar PDF
                </button>
              </div>

              {/* Secondary action buttons */}
              <div className="secondary-button-container">
                <button 
                  className="secondary-button" 
                  onClick={saveNewsletter}
                  disabled={loading || sendingInProgress}
                >
                  Nieuwsbrief Opslaan
                </button>
                <button 
                  className="secondary-button" 
                  onClick={testSendToSelf}
                  disabled={loading || sendingInProgress}
                >
                  Test Verzenden
                </button>
                <button 
                  className="secondary-button" 
                  onClick={toggleExamplesModal}
                  disabled={loading || sendingInProgress}
                >
                  Opgeslagen Nieuwsbrieven
                </button>
                <button 
                  className="secondary-button" 
                  onClick={toggleStatisticsModal}
                  disabled={loading || sendingInProgress}
                >
                  Statistieken
                </button>
              </div>
            </div>
            
            {/* HTML Preview */}
            <div className="preview-container">
              <div className="preview-header">
                <h3>Voorvertoning</h3>
              </div>
              <HtmlPreview content={generateEmailHtml(content)} ref={htmlPreviewRef} />
            </div>
          </div>
        </div>
        
        <CustomModal isOpen={isEmailSelectorOpen} onClose={toggleEmailSelector}>
          <h2>Selecteer Ontvangers</h2>
          <EmailSelector onSelectEmails={handleSelectEmails} />
        </CustomModal>

        <CustomModal isOpen={showExamplesModal} onClose={toggleExamplesModal}>
          <NewsletterExamples onSelectNewsletter={handleSelectNewsletter} />
        </CustomModal>

        <CustomModal isOpen={showStatisticsModal} onClose={toggleStatisticsModal}>
          <Statistics />
        </CustomModal>

        <Modal show={showModal} onClose={() => !sendingInProgress && setShowModal(false)}>
          <h2>Verzendlog</h2>
          {sendingInProgress && (
            <div className="sending-progress">
              {currentEmailIndex > 0 && totalEmails > 0 && (
                <div className="progress-bar-container">
                  <div className="progress-label">
                    Verzenden: {currentEmailIndex}/{totalEmails} 
                    ({Math.round((currentEmailIndex / totalEmails) * 100)}%)
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-bar-fill" 
                      style={{ width: `${(currentEmailIndex / totalEmails) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="log-container">
            {log.map((entry, index) => (
              <div 
                key={index} 
                className={`log-entry ${entry.includes('❌') ? 'error' : ''} ${entry.includes('✅') ? 'success' : ''}`}
              >
                {entry}
              </div>
            ))}
            
            {sendingInProgress && (
              <div className="sending-progress-indicator">
                <div className="spinner-small"></div>
                <span>Bezig met verzenden...</span>
              </div>
            )}
          </div>
          
          {summary && !sendingInProgress && (
            <div className="summary-container">
              <h3>Overzicht verzonden e-mails</h3>
              <div className="summary-stats">
                <p>
                  <span className="stat-label">Totaal:</span> 
                  <span className="stat-value">{summary.length}</span>
                </p>
                <p>
                  <span className="stat-label">Succesvol:</span> 
                  <span className="stat-value success">
                    {summary.filter(r => r.status === 'Succes').length}
                  </span>
                </p>
                <p>
                  <span className="stat-label">Mislukt:</span> 
                  <span className="stat-value error">
                    {summary.filter(r => r.status === 'Mislukt').length}
                  </span>
                </p>
              </div>
              
              <div className="recipients-list">
                {summary.map((result, index) => (
                  <div key={index} className={`recipient-item ${result.status === 'Mislukt' ? 'error' : 'success'}`}>
                    <span className="recipient-email">{result.email}</span>
                    <span className="recipient-status">{result.status}</span>
                    {result.status === 'Mislukt' && <span className="recipient-error">{result.error}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="modal-actions">
            <button 
              onClick={() => setShowModal(false)} 
              className="secondary-button"
              disabled={sendingInProgress}
            >
              Sluiten
            </button>
          </div>
        </Modal>
        
        {/* Loading overlay */}
        {loading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
            <p>Even geduld...</p>
          </div>
        )}
      </main>
      </BeheerdersportaalSidebar>
    </div>
  );
}

export default Newsletter;